/**
 * Tenant Context
 *
 * Provides tenant state management for multitenancy support.
 * Handles tenant selection, switching, and persistence.
 */

import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { api } from '../services/authenticatedApi';
import { TokenManager } from '../utils/tokenManager';
import { useJWTAuth } from './useJWTAuth';
import { logger } from '../utils/logger';
import {
  TenantContext,
  type CreateTenantParams,
  type Tenant,
  type TenantContextType,
} from './tenantContextInstance';

export type { Tenant } from './tenantContextInstance';

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { isAuthenticated } = useJWTAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMultitenancyEnabled, setIsMultitenancyEnabled] = useState(false);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch user's tenants from API
  const refreshTenants = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get('/tenants');
      const { tenants: fetchedTenants, multitenancyEnabled } = response.data;

      setTenants(fetchedTenants || []);
      setIsMultitenancyEnabled(multitenancyEnabled ?? false);

      // If we have tenants, select one
      if (fetchedTenants && fetchedTenants.length > 0) {
        // Try to restore previously selected tenant
        const savedTenantId = TokenManager.getTenantId();
        const savedTenant = savedTenantId
          ? fetchedTenants.find((t: Tenant) => t.id === savedTenantId)
          : null;

        if (savedTenant) {
          setCurrentTenant(savedTenant);
          TokenManager.setTenantId(savedTenant.id);
        } else {
          // Default to first tenant (usually the personal or primary one)
          setCurrentTenant(fetchedTenants[0]);
          TokenManager.setTenantId(fetchedTenants[0].id);
        }
      } else {
        setCurrentTenant(null);
        TokenManager.clearTenantId();
      }
    } catch (err: unknown) {
      // If endpoint returns 404 or feature is disabled, that's OK - just disable multitenancy
      const axiosErr = err as {
        response?: { status?: number; data?: { error?: string } };
        message?: string;
      };
      if (axiosErr.response?.status === 404 || axiosErr.response?.status === 501) {
        setIsMultitenancyEnabled(false);
        setTenants([]);
        setCurrentTenant(null);
      } else {
        const message =
          axiosErr.response?.data?.error || axiosErr.message || 'Failed to load tenants';
        setError(message);
        logger.error('[TenantContext] Failed to fetch tenants:', { error: err });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Switch to a different tenant
  const switchTenant = useCallback(
    async (tenantId: string) => {
      const tenant = tenants.find((t) => t.id === tenantId);

      if (!tenant) {
        setError('Tenant not found');
        return;
      }

      setCurrentTenant(tenant);
      TokenManager.setTenantId(tenantId);

      // Emit event for other components to react to tenant change
      window.dispatchEvent(
        new CustomEvent('tenant:changed', {
          detail: { tenantId, tenant },
        }),
      );
    },
    [tenants],
  );

  // Create a new tenant
  const createTenant = useCallback(async (params: CreateTenantParams): Promise<Tenant> => {
    try {
      const response = await api.post('/tenants', params);
      const { tenant } = response.data;

      // Add to local tenants list
      const newTenant: Tenant = {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        role: tenant.role || 'owner',
        createdAt: tenant.createdAt,
      };

      setTenants((prev) => [...prev, newTenant]);

      // Switch to the new tenant
      setCurrentTenant(newTenant);
      TokenManager.setTenantId(newTenant.id);

      // Emit event
      window.dispatchEvent(
        new CustomEvent('tenant:created', {
          detail: { tenant: newTenant },
        }),
      );

      return newTenant;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.response?.data?.error ||
        'Failed to create team';
      throw new Error(message);
    }
  }, []);

  // Fetch tenants when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      refreshTenants();
    } else {
      // Clear tenant state when not authenticated
      setTenants([]);
      setCurrentTenant(null);
      setIsLoading(false);
      TokenManager.clearTenantId();
    }
  }, [isAuthenticated, refreshTenants]);

  // Listen for auth events to refresh tenants (for external triggers)
  useEffect(() => {
    const handleAuthChange = () => {
      if (isAuthenticated) {
        refreshTenants();
      }
    };

    // Refresh when user signs in
    window.addEventListener('auth:signed-in', handleAuthChange);

    // Clear when user signs out
    const handleSignOut = () => {
      setTenants([]);
      setCurrentTenant(null);
      TokenManager.clearTenantId();
    };
    window.addEventListener('auth:signed-out', handleSignOut);

    return () => {
      window.removeEventListener('auth:signed-in', handleAuthChange);
      window.removeEventListener('auth:signed-out', handleSignOut);
    };
  }, [isAuthenticated, refreshTenants]);

  const value: TenantContextType = {
    currentTenant,
    tenants,
    isLoading,
    error,
    switchTenant,
    createTenant,
    refreshTenants,
    clearError,
    hasMultipleTenants: tenants.length > 1,
    isMultitenancyEnabled,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
